import { useCallback, useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useWriteContract } from 'wagmi';
import { useAppKitAccount } from '@reown/appkit/react';
import { provider, bestiariumAddress, cranniAddress, CRANNILuckBlocksAddress } from '../../.config';
import BestiariumABI from '../../abi/Bestiarium.json';
import ERC20ABI from '../../abi/ERC20_ABI.json';
import { decodeKey } from './animals';
import { storedReferrer } from '../../referral';

const REFRESH_MS = 20000;
const CRANNI_DECIMALS = 8;
const RESULTS_SHOWN = 6;
const MY_BETS_SHOWN = 60;
// CRANNI/USD (6 decimals) from the CRANNI lottery, the same price the token page shows
const PRICE_ABI = ['function getLatestPrice() view returns (uint256)'];

const toCranni = (value) => parseFloat(ethers.utils.formatUnits(value.toString(), CRANNI_DECIMALS));
const settle = (promise, fallback = null) => Promise.resolve(promise).catch(() => fallback);

/**
 * Reads and writes of the Cranni Bestiarium: the draw taking bets, the bankroll and its per-number cap, the latest
 * results and the connected wallet's bets with their prizes.
 */
const useBestiarium = () => {
  const { address, isConnected } = useAppKitAccount();
  const { writeContractAsync } = useWriteContract();
  const [state, setState] = useState({
    loading: true,
    error: null,
    drawId: 0,
    closeTime: 0,
    bankroll: 0,
    maxPayout: 0,
    minBet: 0,
    multipliers: [],
    results: [],
    myBets: [],
    balance: 0,
    allowance: 0,
    chainOffset: 0,
    cranniUsd: 0,
  });

  const refresh = useCallback(async () => {
    if (!bestiariumAddress) {
      setState((s) => ({ ...s, loading: false, error: 'not-deployed' }));
      return;
    }
    try {
      const game = new ethers.Contract(bestiariumAddress, BestiariumABI.abi, provider);
      const priceSource = CRANNILuckBlocksAddress ? new ethers.Contract(CRANNILuckBlocksAddress, PRICE_ABI, provider) : null;
      const [current, bankroll, maxPayout, minBet, multipliers, openDraw, latest, price] = await Promise.all([
        game.currentDraw(),
        game.bankroll(),
        game.maxPayoutPerNumber(),
        game.minBet(),
        game.getMultipliers(),
        game.openDraw(),
        provider.getBlock('latest'),
        priceSource ? settle(priceSource.getLatestPrice()) : null,
      ]);

      // latest settled draws with bets, newest first
      const ids = [];
      for (let id = openDraw.toNumber(); id >= 0 && ids.length < RESULTS_SHOWN * 2; id--) ids.push(id);
      const draws = {};
      await Promise.all(ids.map(async (id) => {
        const d = await settle(game.getDraw(id));
        if (d) draws[id] = { id, closeTime: d.closeTime.toNumber(), settled: d.settled, numbers: d.numbers.map(Number), stakes: d.stakes, scale: d.payoutScale };
      }));
      const results = ids.map((id) => draws[id]).filter((d) => d && d.settled && !d.stakes.isZero()).slice(0, RESULTS_SHOWN);

      let myBets = [];
      let balance = 0;
      let allowance = 0;
      if (address) {
        const token = new ethers.Contract(cranniAddress, ERC20ABI.abi, provider);
        const [betIds, bal, allow] = await Promise.all([
          game.betIdsOf(address),
          settle(token.balanceOf(address), ethers.constants.Zero),
          settle(token.allowance(address, bestiariumAddress), ethers.constants.Zero),
        ]);
        balance = toCranni(bal);
        allowance = toCranni(allow);
        const recent = betIds.slice(-MY_BETS_SHOWN).reverse();
        myBets = await Promise.all(recent.map(async (betId) => {
          const [bet, payout] = await Promise.all([game.getBet(betId), settle(game.payoutOf(betId), ethers.constants.Zero)]);
          const drawId = Number(bet.drawId);
          if (!draws[drawId]) {
            const d = await settle(game.getDraw(drawId));
            if (d) draws[drawId] = { id: drawId, settled: d.settled, numbers: d.numbers.map(Number) };
          }
          return {
            id: betId.toNumber(),
            drawId,
            betType: Number(bet.betType),
            numbers: decodeKey(Number(bet.betType), Number(bet.key)),
            stake: toCranni(bet.stake),
            payoutPerHit: toCranni(bet.payoutPerHit),
            claimed: bet.claimed,
            settled: !!draws[drawId]?.settled,
            payout: toCranni(payout),
          };
        }));
      }

      setState({
        loading: false,
        error: null,
        drawId: current.id.toNumber(),
        closeTime: current.closeTime.toNumber(),
        bankroll: toCranni(bankroll),
        maxPayout: toCranni(maxPayout),
        minBet: toCranni(minBet),
        multipliers: multipliers.map((m) => Number(m) / 100),
        results,
        myBets,
        balance,
        allowance,
        chainOffset: latest ? latest.timestamp * 1000 - Date.now() : 0,
        cranniUsd: price ? parseFloat(ethers.utils.formatUnits(price, 6)) : 0,
      });
    } catch (error) {
      setState((s) => ({ ...s, loading: false, error: error?.message || 'error' }));
    }
  }, [address]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  /** Prize still available on a number of the current draw (stake limit = this / multiplier) */
  const remainingPayout = useCallback(async (betType, numbers) => {
    if (!bestiariumAddress) return null;
    const game = new ethers.Contract(bestiariumAddress, BestiariumABI.abi, provider);
    const value = await settle(game.remainingPayout(betType, [numbers[0] ?? 0, numbers[1] ?? 0, numbers[2] ?? 0]));
    return value ? toCranni(value) : null;
  }, []);

  // `onSent(hash)` runs once the wallet signed, while the transaction waits for the network
  const settleWrite = useCallback(
    async (hash, onSent) => {
      if (hash) {
        onSent?.(hash);
        const receipt = await provider.waitForTransaction(hash).catch(() => null);
        if (receipt && receipt.status === 0) {
          await refresh();
          throw Object.assign(new Error('reverted'), { reverted: true });
        }
      }
      await refresh();
      return hash;
    },
    [refresh]
  );

  const approve = useCallback(
    async (amount, onSent) => {
      const hash = await writeContractAsync({
        address: cranniAddress,
        abi: ERC20ABI.abi,
        functionName: 'approve',
        args: [bestiariumAddress, ethers.utils.parseUnits(amount.toFixed(CRANNI_DECIMALS), CRANNI_DECIMALS).toBigInt()],
      });
      return settleWrite(hash, onSent);
    },
    [writeContractAsync, settleWrite]
  );

  const placeBets = useCallback(
    async (slip, onSent) => {
      const hash = await writeContractAsync({
        address: bestiariumAddress,
        abi: BestiariumABI.abi,
        functionName: 'placeBets',
        args: [
          slip.map((b) => ({
            betType: b.betType,
            numbers: [b.numbers[0] ?? 0, b.numbers[1] ?? 0, b.numbers[2] ?? 0],
            stake: ethers.utils.parseUnits(b.stake.toFixed(CRANNI_DECIMALS), CRANNI_DECIMALS).toBigInt(),
          })),
          // the platform-wide referral link (only recorded while the player has no referrer)
          storedReferrer(address) || ethers.constants.AddressZero,
        ],
      });
      return settleWrite(hash, onSent);
    },
    [writeContractAsync, settleWrite, address]
  );

  const claim = useCallback(
    async (betIds, onSent) => {
      const hash = await writeContractAsync({
        address: bestiariumAddress,
        abi: BestiariumABI.abi,
        functionName: 'claim',
        args: [betIds.map((id) => ethers.BigNumber.from(id).toBigInt())],
      });
      return settleWrite(hash, onSent);
    },
    [writeContractAsync, settleWrite]
  );

  return { ...state, address, isConnected, refresh, remainingPayout, approve, placeBets, claim };
};

export default useBestiarium;
